/**
 * Jotai atoms for Chronicle bulk sync state.
 *
 * Purely driven by Rust events — TypeScript never drives the sync pipeline.
 * Updated by `useChronicleBulkSyncEvents` hook.
 */
import { atom } from 'jotai';

import {
  chronicleChunkLiveStopInFlightAtom,
  chronicleSupportsChunkLiveAtom,
} from './chunk-live';
import { chronicleIsRecordingAtom } from './session';

// --- Phase (driven by Rust events) ---

export type BulkSyncPhase =
  | 'idle'
  | 'downloading'
  | 'uploading'
  | 'complete'
  | 'interrupted'
  | 'error';

export const bulkSyncPhaseAtom = atom<BulkSyncPhase>('idle');

// --- Download Progress (from bulk_download:progress events) ---

export type TransferMethod = 'ble' | 'wifi' | 'usb';

export interface BulkSyncDownloadProgress {
  totalFiles: number;
  completedFiles: number;
  currentFileName: string | null;
  currentBytesTransferred: number;
  currentBytesTotal: number;
  currentTransferMethod: TransferMethod;
}

const INITIAL_DOWNLOAD_PROGRESS: BulkSyncDownloadProgress = {
  totalFiles: 0,
  completedFiles: 0,
  currentFileName: null,
  currentBytesTransferred: 0,
  currentBytesTotal: 0,
  currentTransferMethod: 'ble',
};

// --- USB State ---
// These atoms mirror VolumeWatcher hardware events (mount/unmount).
// Currently written by useChronicleBulkSyncEvents; consumers will be added
// when the device card and dev dashboard show USB connection status (APP-3958 follow-up).

export const usbConnectedAtom = atom(false);
export const usbMountPathAtom = atom<string | null>(null);

// APP-4668: Windows-only — set when the OS refuses access to a candidate
// removable volume (typically corp Group Policy / EDR / BitLocker, but the
// copy is intentionally agnostic about cause). Cleared when the same drive
// becomes readable, when it unmounts, or on logout.
//
// `driveType` is the Win32 GetDriveTypeW classification as a string (e.g.
// 'Removable', 'CdRom', 'Other(7)') — kept as a free-form string so we can
// add new variants in Rust without breaking the TS contract.
//
// Read by useChronicleNotifications to surface a warning modal and by the
// Amplitude tracker to cohort failure modes.
export interface UsbAccessDeniedState {
  mountPath: string;
  osError: number | null;
  driveType: string | null;
}

export const usbAccessDeniedAtom = atom<UsbAccessDeniedState | null>(null);

// UI-only prompt state. Dismissing the modal should not clear
// usbAccessDeniedAtom, because that atom represents the underlying OS condition.
export const usbAccessDeniedPromptAtom = atom<UsbAccessDeniedState | null>(null);

// --- WiFi State ---
// Mirrors WiFi hotspot connection state from the plugin.
// Written by useChronicleBulkSyncEvents on wifi:* events.

export type WifiConnectionState = 'disconnected' | 'connecting' | 'connected' | 'failed';

export const wifiConnectionStateAtom = atom<WifiConnectionState>('disconnected');
export const wifiSsidAtom = atom<string | null>(null);

export const bulkSyncDownloadProgressAtom = atom<BulkSyncDownloadProgress>(
  INITIAL_DOWNLOAD_PROGRESS
);

// --- Upload Progress (from session_engine:* events) ---

export interface BulkSyncUploadProgress {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
}

const INITIAL_UPLOAD_PROGRESS: BulkSyncUploadProgress = {
  totalFiles: 0,
  completedFiles: 0,
  failedFiles: 0,
};

export const bulkSyncUploadProgressAtom = atom<BulkSyncUploadProgress>(
  INITIAL_UPLOAD_PROGRESS
);

export interface BulkSyncAudioUploadProgress {
  uploadedChunks: number;
  totalChunks: number;
}

const INITIAL_AUDIO_UPLOAD_PROGRESS: BulkSyncAudioUploadProgress = {
  uploadedChunks: 0,
  totalChunks: 0,
};

export const bulkSyncAudioUploadProgressAtom = atom<BulkSyncAudioUploadProgress>(
  INITIAL_AUDIO_UPLOAD_PROGRESS
);

// Termination denominator for the session-creation engine. Accumulates
// `downloaded` across overlapping bulk-download batches and resets to 0
// when the engine fully drains. Engine's own `receivedTasks` resets per
// queue drain so it can't be used as a stable denominator.
export const bulkSyncExpectedUploadTotalAtom = atom<number>(0);

// Cross-batch numerator for the termination gate. The engine resets its
// per-batch counters after every `session_engine:all_complete` emission, so
// `uploadProgress.completedFiles` (overwritten by per-batch
// `processedTasks` on every event) cannot be compared against the
// cross-batch `bulkSyncExpectedUploadTotalAtom`. Accumulating
// `totalProcessed` from each `all_complete` keeps both sides of the gate
// in the same scope. Reset in lockstep with `bulkSyncExpectedUploadTotalAtom`.
export const bulkSyncCumulativeProcessedAtom = atom<number>(0);

// Internal run-scoped state read by `useChronicleBulkSyncEvents`' completion
// gate. Keep this in atoms, not hook refs, so a provider/hook remount does not
// strand an active run while the widget counters still show "finalizing N/M".
export const bulkSyncCompletedTaskIdsAtom = atom<Set<string>>(new Set<string>());
export const bulkSyncCompletedTaskCountAtom = atom(0);
export const bulkSyncDownloadCompleteAtom = atom(false);
export const bulkSyncDiscoveredMaxAtom = atom(0);
export const bulkSyncPendingDrainCountAtom = atom(0);
// Engine tasks that started but have not completed/failed yet. In an atom (not
// a hook ref) so a remount can't demote a still-live upload to the short idle
// stall budget and false-resolve it.
export const bulkSyncActiveEngineTaskIdsAtom = atom<Set<string>>(new Set<string>());

// --- Note generation failures (best-effort, non-blocking) ---

export const bulkSyncNoteFailedCountAtom = atom(0);

// --- Error ---

export interface BulkSyncError {
  phase: 'download' | 'decrypt' | 'upload';
  /** Pre-formatted count copy. Absent when `errorCode` carries the reason. */
  message?: string;
  failedFiles: number;
  canRetry: boolean;
  /** Rust `error_code`; the widget renders localised copy from it. */
  errorCode?: string;
}

export const bulkSyncErrorAtom = atom<BulkSyncError | null>(null);

// --- Derived ---

// Widget stays visible across the entire bulk-sync lifecycle (APP-5704).
// It is a global indicator (rendered in `deferred-global-widgets`) — without
// the `'uploading'` branch the indicator would disappear once
// `bulk_download:complete` fires while the engine is still processing,
// leaving the user with no signal on pages other than the session list
// (where the contextual `SessionCreationInProgressBanner` shows).
export const showBulkSyncWidgetAtom = atom((get) => {
  const phase = get(bulkSyncPhaseAtom);
  // On chunk-live, backlog sync can run *during* a recording (HAR-1548 two
  // lanes), and a live "Syncing" indicator mid-recording reads as "something
  // is wrong with my recording" — with nothing the clinician can do about it.
  // The active lane has priority, so the recording is unaffected.
  //
  // The stop drain is the same window to the clinician: `chronicleIsRecording`
  // drops the instant the stop starts, so suppression must also span the
  // in-flight stop or the indicator pops on a session that is ending normally.
  //
  // Only the in-flight phases are suppressed. `error` / `interrupted` /
  // `complete` are terminal notices about OTHER sessions, and hiding a failure
  // while the device is still in range removes the one window in which a retry
  // is possible.
  //
  // Firmware-gated so pre-5.0 is untouched: unresolved / unparseable /
  // no-device-seen all resolve false on `chronicleSupportsChunkLiveAtom`.
  if (
    (phase === 'downloading' || phase === 'uploading') &&
    (get(chronicleIsRecordingAtom) || get(chronicleChunkLiveStopInFlightAtom)) &&
    get(chronicleSupportsChunkLiveAtom)
  ) {
    return false;
  }
  return (
    phase === 'downloading' ||
    phase === 'uploading' ||
    phase === 'complete' ||
    phase === 'interrupted' ||
    phase === 'error'
  );
});

/**
 * True when a bulk sync download or upload is in flight.
 * Terminal phases (`complete` / `interrupted` / `error`) and `idle` are false.
 */
export const isBulkSyncActiveAtom = atom((get) => {
  const phase = get(bulkSyncPhaseAtom);
  return phase === 'downloading' || phase === 'uploading';
});

/**
 * True when the device WiFi hotspot is connected (no internet available).
 *
 * macOS routes all traffic through the device hotspot during WiFi sync,
 * which causes API calls, WebSocket connections, and other network requests
 * to fail. Consumers should check this before logging network errors.
 */
export const isWifiSyncActiveAtom = atom((get) => {
  const wifiState = get(wifiConnectionStateAtom);
  return wifiState === 'connected' || wifiState === 'connecting';
});

// --- Reset ---
// USB atoms (usbConnectedAtom, usbMountPathAtom) are excluded because they
// reflect hardware mount state that persists across sync runs.
//
// WiFi state IS reset because the backend may fail to emit `wifi:disconnected`
// when the download future is cancelled (async cancellation drops the
// WifiDownloader). Resetting here acts as a safety net so the "Switch to
// WiFi" button always reappears on the next sync.

export const resetBulkSyncAtomsAtom = atom(null, (_get, set) => {
  set(bulkSyncPhaseAtom, 'idle');
  set(bulkSyncDownloadProgressAtom, INITIAL_DOWNLOAD_PROGRESS);
  set(bulkSyncUploadProgressAtom, INITIAL_UPLOAD_PROGRESS);
  set(bulkSyncAudioUploadProgressAtom, INITIAL_AUDIO_UPLOAD_PROGRESS);
  set(bulkSyncExpectedUploadTotalAtom, 0);
  set(bulkSyncCumulativeProcessedAtom, 0);
  set(bulkSyncCompletedTaskIdsAtom, new Set<string>());
  set(bulkSyncCompletedTaskCountAtom, 0);
  set(bulkSyncActiveEngineTaskIdsAtom, new Set<string>());
  set(bulkSyncDownloadCompleteAtom, false);
  set(bulkSyncDiscoveredMaxAtom, 0);
  set(bulkSyncPendingDrainCountAtom, 0);
  set(bulkSyncErrorAtom, null);
  set(bulkSyncNoteFailedCountAtom, 0);
  set(wifiConnectionStateAtom, 'disconnected');
  set(wifiSsidAtom, null);
});
